import React from "react";
import { Card, CardContent } from "../../features/ui/card";
import { Skeleton } from "../../features/ui/skeleton";
import { DollarSign, Percent, ShoppingCart, TrendingUp } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../features/ui/table";

interface ManagerialLoaderProps {
    variant?: "revenue-card" | "profit-card" | "margin-card" | "forecast-card" | "managerial-chart" | "channel-perf" 
            | "channel-comp" | "product-stats" | "product-cycle" | "ai-reco";
    className?: string;
    itemCount?: number; // Optional: customizable item count
}


const ManagerialLoader: React.FC<ManagerialLoaderProps> = ({ variant = "list", className = "", itemCount }) => {
    switch (variant) {
        case "revenue-card":
            return (
                <CardContent className="p-6">
                    <div className={className} aria-busy="true" role="status">
                        <div className="flex items-center justify-between space-y-0 pb-2">
                            <p className="text-sm font-medium text-muted-foreground">
                                Monthly Revenue
                            </p>
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <Skeleton className="h-6 w-32 bg-secondary" />
                        <div className="flex items-center pt-2 space-x-2">
                            <Skeleton className="h-3 w-40 bg-secondary" />
                        </div>
                    </div>
                </CardContent>
            )
        case "profit-card":
            return (
                <CardContent className="p-6">
                    <div className={className} aria-busy="true" role="status">
                        <div className="flex items-center justify-between space-y-0 pb-2">
                            <p className="text-sm font-medium text-muted-foreground">
                                Monthly Profit
                            </p>
                            <TrendingUp className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <Skeleton className="h-6 w-32 bg-secondary" />
                        <div className="flex items-center pt-2 space-x-2">
                            <Skeleton className="h-3 w-40 bg-secondary" />
                        </div>
                    </div>
                </CardContent>
            )
        case "margin-card":
            return (
                <CardContent className="p-6">
                    <div className={className} aria-busy="true" role="status">
                        <div className="flex items-center justify-between space-y-0 pb-2">
                            <p className="text-sm font-medium text-muted-foreground">
                                Profit Margin
                            </p>
                            <Percent className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <Skeleton className="h-6 w-32 bg-secondary" />
                        <div className="flex items-center pt-2 space-x-2">
                            <Skeleton className="h-3 w-40 bg-secondary" />
                        </div>
                    </div>
                </CardContent>
            )
        case "forecast-card":
            return (
                <CardContent className="p-6">
                    <div className={className} aria-busy="true" role="status">
                        <div className="flex items-center justify-between space-y-0 pb-2">
                            <p className="text-sm font-medium text-muted-foreground">
                                Monthly Forecast
                            </p>
                            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <Skeleton className="h-6 w-32 bg-secondary" />
                        <div className="flex items-center pt-2 space-x-2">
                            <Skeleton className="h-3 w-40 bg-secondary" />
                        </div>
                    </div>
                </CardContent>
            )
        case "managerial-chart":
            return (
                <div className={className} aria-busy="true" role="status">
                    <div className="flex flex-row px-6 pt-6 space-x-4">
                        {/* Y-axis bar */}
                        <Skeleton className="h-80 w-6 rounded-lg bg-secondary" />
                        {/* Simulated chart area */}
                        <Skeleton className="h-70 w-full rounded-lg bg-secondary" />
                    </div>
                    {/* Bottom axis labels */}
                    <div className="px-6 pt-4 pb-4">
                        <Skeleton className="h-4 w-full rounded-md bg-secondary" />
                    </div>
                    <div className="flex justify-center space-x-2 mb-4  ">
                        <Skeleton className="h-6 w-14 bg-secondary" />
                        <Skeleton className="h-6 w-14 bg-secondary" />
                    </div>

                </div>
            )
        case "channel-perf":
            return (
                <Card className="bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft w-full">
                    <div className={className} aria-busy="true" role="status">
                        <div className="flex flex-row px-6 pt-6 space-x-4">
                            {/* Y-axis bar */}
                            <Skeleton className="h-80 w-6 rounded-lg bg-secondary" />
                            {/* Simulated chart area */}
                            <Skeleton className="h-70 w-full rounded-lg bg-secondary" />
                        </div>
                        {/* Bottom axis labels */}
                        <div className="px-6 pt-4 pb-4">
                            <Skeleton className="h-4 w-full rounded-md bg-secondary" />
                        </div>
                        <div className="flex justify-center space-x-2 m-3  ">
                            <Skeleton className="h-6 w-14 bg-secondary" />
                            <Skeleton className="h-6 w-14 bg-secondary" />
                            <Skeleton className="h-6 w-14 bg-secondary" />
                            <Skeleton className="h-6 w-14 bg-secondary" />
                            <Skeleton className="h-6 w-14 bg-secondary" />
                        </div>
                    </div>
                </Card>
            )
        case "channel-comp":
            return (
                <Card className="bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft overflow-hidden" style={{ maxHeight: "615px", overflowY: "auto" }} aria-busy="true" role="status">
                    <div className="m-2">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Channel</TableHead>
                                    <TableHead>Revenue</TableHead>
                                    <TableHead>Orders</TableHead>
                                    <TableHead>Conv. Rate</TableHead>
                                    <TableHead>AOV</TableHead>
                                    <TableHead>Growth</TableHead>
                                    <TableHead>Share</TableHead>


                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {[...Array(5)].map((_, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                        <TableCell><Skeleton className="h-4 w-24 bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-12 rounded-full bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-20 bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16 bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16 bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16 bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-10 bg-secondary" /></TableCell>

                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            )
        case "product-stats":
            return (
                <Card className="bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft w-full">
                    <div className={className} aria-busy="true" role="status">
                        <div className="flex justify-center items-center h-[310px] m-6">
                            <div className="relative">
                                <Skeleton className="w-[200px] h-[200px] rounded-full bg-secondary" />
                            </div>
                        </div>
                        <div className="flex flex-row justify-center space-x-3 m-2">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="flex flex-row items-center space-x-1">
                                    <div className="flex flex-col items-center space-y-2">
                                        <Skeleton className="h-12 w-12 rounded-full bg-secondary" />
                                        <Skeleton className="h-4 w-24 rounded bg-secondary" />
                                        <Skeleton className="h-4 w-16 rounded bg-secondary" />
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div>
                </Card>
            )
        case "product-cycle":
            return (
                <Card className="bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft overflow-hidden" style={{ maxHeight: "800px", overflowY: "auto" }} aria-busy="true" role="status">
                    <div className="m-2">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Product</TableHead>
                                    <TableHead>Category</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Inventory</TableHead>
                                    <TableHead>Sales Velocity</TableHead>
                                    <TableHead>Days of Supply</TableHead>
                                    <TableHead>Profit Margin</TableHead>
                                    <TableHead>Monthly Revenue</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {[...Array(10)].map((_, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                        <TableCell>
                                            <div className="flex flex-col space-y-1">
                                                <Skeleton className="h-4 w-24 bg-secondary" />
                                                <Skeleton className="h-4 w-10 bg-secondary" />
                                            </div>
                                        </TableCell>
                                        <TableCell><Skeleton className="h-5 w-12 rounded-full bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-20 bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16 bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16 bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16 bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-10 bg-secondary" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-10 bg-secondary" /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            )
        case "ai-reco":
            return(
                <div className="space-y-6">
                          <div className="space-y-3" style={{ maxHeight: "300px", overflowY: "auto" }}>
                            {Array(itemCount ?? 3).fill(0).map((_, i) => (
                              <div
                                key={i}
                                className="flex items-start justify-between rounded-lg border p-3 animate-pulse"
                              >
                                <div>
                                  <Skeleton className="h-4 w-32 mb-1" />
                                  <Skeleton className="h-3 w-12 mb-1" />
                                  <Skeleton className="h-4 w-40" />
                                </div>
                                <div className="flex flex-col items-end">
                                  <Skeleton className="h-4 w-24 mb-1" />
                                  <Skeleton className="h-4 w-20" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
            )    
    }


}

export default ManagerialLoader